import { getItemThumbnail } from "./dataSourceAdapters";
// Remove the circular dependency import
// import {
//   AudioPreview,
//   AudioThumbnail,
//   DocumentPreview,
//   DocumentThumbnail,
//   ImagePreview,
//   ImageThumbnail,
//   MediaPlaceholder,
//   PDFPreview,
//   PDFThumbnail,
//   VideoPreview,
//   VideoThumbnail
// } from '../components/media/MediaComponents';
import { MediaResponse, MediaItemDetailed } from "../types";
import { FILE_EXTENSIONS } from "./mediaTypes";
import { assetDisplayUrl, embedSrc, isApiMedia, isEmbedAsset } from "./mediaAsset";
// TEMPORARY: Import thumbnail construction utilities (remove when backend provides URLs)

export type MediaType = "image" | "pdf" | "audio" | "video" | "document" | "embed" | "unknown";

export interface MediaPreviewProps {
  url: string;
  title?: string;
  onClose?: () => void;
  onLoad?: () => void;
  onError?: () => void;
  scale?: number;
  width?: string | number;
  height?: string | number;
}

function getItemType(type: string) {
  // 🚨 FIX: Handle incorrect MIME types from server
  if (type === "model/vnd.mts") return "video";

  if (type.startsWith("embed/") || type === "embed") return "embed";
  if (type.includes("pdf") || type === "application/pdf") return "pdf";
  if (type === "photo" || type === "image" || type.startsWith("image/")) return "image";
  if (type === "video" || type.startsWith("video/")) return "video";
  if (type === "audio" || type.startsWith("audio/")) return "audio";
  if (type.includes("document") || type === "application/msword" || type.includes("officedocument")) return "document";
}

function detectMediaTypeFromUrl(baseUrl: string) {
  const normalized = baseUrl.split("?")[0].toLowerCase();

  const imagePattern = new RegExp(`\\.(${FILE_EXTENSIONS.IMAGE.join("|")})$`, "i");
  const audioPattern = new RegExp(`\\.(${FILE_EXTENSIONS.AUDIO.join("|")})$`, "i");
  const videoPattern = new RegExp(`\\.(${FILE_EXTENSIONS.VIDEO.join("|")})$`, "i");
  const documentPattern = new RegExp(`\\.(${FILE_EXTENSIONS.DOCUMENT.join("|")})$`, "i");

  if (imagePattern.test(normalized)) return "image";
  if (/\.pdf$/i.test(normalized)) return "pdf";
  if (audioPattern.test(normalized)) return "audio";
  if (videoPattern.test(normalized)) return "video";
  if (documentPattern.test(normalized)) return "document";

  return null;
}

// Get media type with better URL handling
export const getMediaType = (item: MediaResponse | string): MediaType => {
  // API media says what it is. "other" (zip, csv, docx…) falls through to the MIME and
  // filename checks, which pick a document icon or "unknown".
  if (isApiMedia(item) && item.media_kind !== "other") return item.media_kind;

  // For server-returned MediaResponse objects
  if (typeof item !== "string" && item) {
    // 🚨 IMPORTANT: This function contains fixes for incorrect server MIME types
    // The server sometimes returns 'model/vnd.mts' for MTS video files, which is incorrect
    // The correct MIME type should be 'video/mp2t' or 'application/octet-stream'
    // We handle this by:
    // 1. Directly correcting known incorrect MIME types
    // 2. Adding incorrect MIME types to the "generic" list to trigger filename fallback
    // 3. Enhanced debugging to identify infinite loops caused by unrecognized types

    // First check media_type property
    if (item.media_type) {
      const type = item.media_type.toLowerCase();

      const mediaType = getItemType(type);
      if (mediaType) return mediaType;
    }

    // Then check the original file's MIME type
    const mimeType = item.original_file?.type;
    if (mimeType) {
      const mime = mimeType.toLowerCase();

      const mediaType = getItemType(mime);
      if (mediaType) return mediaType;
    }

    // IMPORTANT: Fallback to filename extension checking for generic/incorrect MIME types
    // This handles cases where server returns 'application/octet-stream' or incorrect MIME types for video files
    const isGenericMimeType =
      !item.media_type ||
      item.media_type === "application/octet-stream" ||
      item.media_type === "model/vnd.mts" || // Handle incorrect MTS MIME type
      (mimeType && (mimeType === "application/octet-stream" || mimeType === "binary/octet-stream" || mimeType === "model/vnd.mts")); // Handle incorrect MTS MIME type

    if (isGenericMimeType) {
      // Try to get filename from various sources
      const filename = item.original_file?.filename || item.url || "";

      if (filename) {
        // Use the same extension checking logic as for strings
        const baseUrl = filename.split("?")[0].toLowerCase();

        const detectType = detectMediaTypeFromUrl(baseUrl);
        if (detectType) return detectType;
      }
    }
  }

  // For strings (filenames/URLs) only check extension for local files
  if (typeof item === "string") {
    // Extract the base filename without query parameters
    const baseUrl = item.split("?")[0].toLowerCase();

    const detectType = detectMediaTypeFromUrl(baseUrl);
    if (detectType) return detectType;
  }

  // Check from properties
  if (typeof item !== "string" && "properties" in item && item?.properties) {
    const properties = item.properties as Record<string, string>;

    // check for filename or url
    const filename = properties?.filename || properties.url || "";
    if (filename) {
      const baseUrl = filename?.split("?")[0].toLowerCase();
      const detectType = detectMediaTypeFromUrl(baseUrl);
      if (detectType) return detectType;
    }

    // Check from media_type
    if (properties?.media_type) {
      const mediaType = getItemType(properties?.media_type);
      if (mediaType) return mediaType;
    }
  }

  // An embed without a media_type still has an embed src
  if (typeof item !== "string" && item && embedSrc(item)) return "embed";

  return "unknown";
};

// Normalize URLs for media display
export const constructMediaUrl = (url: string): string => {
  if (!url) return "";

  // If it's already a blob URL or data URL, return as is
  if (url.startsWith("blob:") || url.startsWith("data:")) {
    return url;
  }

  // If it's already an absolute URL, return as is
  if (url.startsWith("http:") || url.startsWith("https:")) {
    return url;
  }

  // Otherwise, construct a URL relative to the current origin
  return `${window.location.origin}${url.startsWith("/") ? "" : "/"}${url}`;
};

/**
 * Checks if a URL is absolute
 * @param url The URL to check
 * @returns boolean indicating if the URL is absolute
 */
export function isAbsoluteUrl(url: string): boolean {
  if (!url) return false;
  return url.startsWith("http://") || url.startsWith("https://");
}

// Helper function to check if a file is previewable
export function isPreviewable(filename: string): boolean {
  const mediaType = getMediaType(filename);
  return mediaType !== "unknown";
}

// Helper function to get the appropriate thumbnail icon
export function getThumbnailIcon(filename: string): string {
  const mediaType = getMediaType(filename);
  switch (mediaType) {
    case "pdf":
      return "/icons/doc-icon.svg";
    case "video":
      return "/icons/play-button.png";
    case "audio":
      return "/icons/volume.png";
    case "document":
      return "/icons/doc-icon.svg";
    case "embed":
      return "/icons/no-image.png";
    default:
      return "/icons/no-image.png";
  }
}

// New utility function to get appropriate media URL
export const getMediaUrl = (item: MediaResponse | MediaItemDetailed | null, size: "thumbnail" | "preview" | "full" = "full"): string => {
  if (!item) return "";

  // Future implementation with URL parameters
  if (process.env.REACT_APP_USE_CDN_PARAMS === "true") {
    const baseUrl = item.url || "";
    // Don't add parameters to already-parametrized URLs
    if (baseUrl.includes("?")) return baseUrl;
    return `${baseUrl}?size=${size}`;
  }

  // API media: what to show is `display_url` (else the original), for every size but the
  // thumbnail. An embed has no file: the viewer renders its display_url in an iframe.
  if (isApiMedia(item)) {
    if (size === "thumbnail") return item.thumbnail || assetDisplayUrl(item);
    return isEmbedAsset(item) ? "" : assetDisplayUrl(item);
  }

  // Current implementation
  switch (size) {
    case "thumbnail":
      return item.thumbnail || item.url || "";
    case "preview":
      // For videos and PDFs, use full URL for preview
      if (item.media_type === "video" || item.media_type === "pdf") {
        return item.url || "";
      }
      // An item still being prepared for upload previews from its local blob
      return (item as MediaItemDetailed).previewUrl || item.thumbnail || item.url || "";
    case "full":
    default:
      return item.url || "";
  }
};

// TEMPORARY: Get a safe thumbnail URL that ensures we only use actual image URLs as thumbnails
// TODO: Remove this when backend provides thumbnail URLs directly
export const getSafeThumbnailUrl = (item: any, endpoint?: any): string | undefined => {
  if (!item) return undefined;

  // Use the new data-source-specific adapter
  const result = getItemThumbnail(item, endpoint);

  // Clean implementation using data-source-specific adapters

  return result || undefined;
};
